import { useEffect, useInsertionEffect, useMemo, useState } from "react";
import type { Camada } from "@foto/shared";
import { carregarFonte, descritoresDasCamadas, injetarFontes } from "./fontes-no-navegador.js";

/**
 * Garante que as fontes dos textos chegaram, e avisa quando chegam.
 *
 * As regras `@font-face` entram na página **antes da primeira pintura** (`useInsertionEffect`, o gancho próprio
 * para injetar CSS): sem isso o primeiro quadro desenharia o texto na fonte de reserva e o corrigiria depois.
 * O arquivo em si chega pela rede, e `font-display: swap` mostra a reserva até lá.
 *
 * O número devolvido sobe cada vez que uma fonte termina de carregar. Quem mede o texto no DOM o usa como
 * dependência e **mede de novo**: o bloco com a fonte de reserva tem outro tamanho que o da fonte de verdade.
 */
export function useFontesDasCamadas(camadas: readonly Camada[]): number {
  const [versao, setVersao] = useState(0);

  useInsertionEffect(() => {
    injetarFontes();
  }, []);

  const descritores = useMemo(
    () => descritoresDasCamadas(camadas.filter((c) => c.tipo === "texto")),
    [camadas],
  );
  const chave = descritores.join("|");

  useEffect(() => {
    let vivo = true;
    for (const d of descritores) {
      void carregarFonte(d, "AaÁáÂâÃãÇçÉéÊêÍíÓóÔôÕõÚú").then(() => {
        if (vivo) setVersao((v) => v + 1);
      });
    }
    return () => {
      vivo = false;
    };
    // `chave` resume `descritores`: só recarrega quando uma combinação nova de fonte aparece.
  }, [chave]);

  return versao;
}
